import { getPublicMarkdownCapabilityDiagnostics, getPublicSiteCapabilityDiagnostics, } from "../core/index.js";
function diagnostic(severity, code, message) {
    return { severity, code, message };
}
function auditRedirects(site) {
    const diagnostics = [];
    const redirects = new Map(site.getRedirectAliases().map((redirect) => [redirect.path, redirect]));
    for (const redirect of redirects.values()) {
        const visited = new Set([redirect.path]);
        let location = redirect.location;
        let depth = 0;
        while (redirects.has(location)) {
            depth++;
            if (visited.has(location)) {
                diagnostics.push(diagnostic("error", "redirect.loop", `Redirect '${redirect.path}' contains a loop through '${location}'.`));
                break;
            }
            visited.add(location);
            location = redirects.get(location).location;
            if (depth > 20) {
                diagnostics.push(diagnostic("error", "redirect.too_deep", `Redirect '${redirect.path}' exceeds 20 hops.`));
                break;
            }
        }
        if (depth > 0 && !diagnostics.some((entry) => entry.message.includes(`'${redirect.path}'`)))
            diagnostics.push(diagnostic("warning", "redirect.chain", `Redirect '${redirect.path}' uses ${depth + 1} hops; point it directly to '${location}'.`));
    }
    return diagnostics;
}
export async function auditPublicSite(options) {
    const diagnostics = [...options.site.validate(), ...auditRedirects(options.site)];
    const seenPaths = new Set();
    for (const path of options.site.getIndexableUrls()) {
        if (seenPaths.has(path))
            diagnostics.push(diagnostic("error", "resource.duplicate_path", `Indexable path '${path}' is declared more than once.`));
        seenPaths.add(path);
        const resolution = options.site.resolvePath(path);
        if (resolution.type !== "render" || !resolution.indexable)
            diagnostics.push(diagnostic("error", "resource.not_renderable", `Indexable path '${path}' does not resolve to an indexable render route.`));
        const alternates = options.site.getAlternatePaths(path);
        const alternateLanguages = new Set();
        for (const alternate of alternates) {
            if (alternateLanguages.has(alternate.hreflang))
                diagnostics.push(diagnostic("error", "hreflang.duplicate", `Path '${path}' declares hreflang '${alternate.hreflang}' more than once.`));
            alternateLanguages.add(alternate.hreflang);
            const alternateResolution = options.site.resolvePath(alternate.path);
            if (alternateResolution.type !== "render" || !alternateResolution.indexable)
                diagnostics.push(diagnostic("error", "hreflang.invalid_target", `Path '${path}' points hreflang '${alternate.hreflang}' to non-indexable '${alternate.path}'.`));
        }
    }
    for (const route of options.site.routerRouteDefinitions)
        if (route.access === "authenticated") {
            if (route.crawl !== "disallow")
                diagnostics.push(diagnostic("error", "private.crawl_allowed", `Authenticated route '${route.key}' must disallow crawling.`));
            if (route.render.kind !== "spa")
                diagnostics.push(diagnostic("warning", "private.server_rendered", `Authenticated route '${route.key}' is configured for '${route.render.kind}' rendering.`));
        }
    if (options.capabilities) {
        for (const missing of getPublicSiteCapabilityDiagnostics(options.site, options.capabilities))
            diagnostics.push(diagnostic("error", "capability.missing", `Missing ${missing}.`));
        for (const missing of getPublicMarkdownCapabilityDiagnostics(options.site, options.capabilities))
            diagnostics.push(diagnostic("error", "markdown.missing", `Missing ${missing}.`));
    }
    if (options.resources) {
        const ids = new Set();
        const paths = new Set();
        for await (const resource of options.resources) {
            if (ids.has(resource.id))
                diagnostics.push(diagnostic("error", "resource.duplicate_id", `Resource id '${resource.id}' is not unique.`));
            if (paths.has(resource.path))
                diagnostics.push(diagnostic("error", "resource.duplicate_path", `Resource path '${resource.path}' is not unique.`));
            ids.add(resource.id);
            paths.add(resource.path);
            const match = options.site.matchRoute(resource.path);
            if (!match || match.route.key !== resource.routeKey)
                diagnostics.push(diagnostic("error", "resource.route_mismatch", `Resource '${resource.id}' does not resolve to route '${resource.routeKey}'.`));
        }
    }
    return diagnostics;
}
export async function assertPublicSiteAudit(options) {
    const diagnostics = await auditPublicSite(options);
    const errors = diagnostics.filter((entry) => entry.severity === "error");
    if (errors.length > 0)
        throw new Error(`Public site audit failed:\n${errors.map((entry) => `- [${entry.code}] ${entry.message}`).join("\n")}`);
    return diagnostics;
}
//# sourceMappingURL=index.js.map